<template>
    <v-dialog
        :value="isOpen"
        max-width="500px"
        transition="slide-y-transition"
        eager
        @keydown.esc="onClose"
        @click:outside="onClose"
    >
        <v-card>
            <v-card-title>
                <h1 class="text-subtitle-1">
                    <template v-if="!isNewCustomerAddress">
                        Edycja adresu
                    </template>
                    <template v-else>
                        Nowy adres
                    </template>
                </h1>
            </v-card-title>
            <v-card-text>
                <v-text-field
                    v-model="customerAddress.companyName"
                    label="Nazwa firmy"
                    outlined
                    dense
                />
                <v-text-field
                    v-model="customerAddress.street"
                    label="Ulica"
                    :error-messages="streetErrors"
                    outlined
                    dense
                    required
                />
                <v-text-field
                    v-model="customerAddress.postCode"
                    label="Kod pocztowy"
                    outlined
                    dense
                    :error-messages="postCodeErrors"
                    required
                />
                <v-text-field
                    v-model="customerAddress.city"
                    label="Miasto"
                    outlined
                    dense
                    :error-messages="cityErrors"
                    required
                />
                <v-select
                    v-model="customerAddress.country"
                    :items="countries"
                    item-value="id"
                    item-text="name"
                    no-data-text="Nie znaleziono kraju"
                    label="Kraj"
                    outlined
                    dense
                    return-object
                    :error-messages="countryErrors"
                    required
                />
                <v-select
                    v-model="customerAddress.addressType"
                    :items="addressTypes"
                    item-value="id"
                    item-text="name"
                    no-data-text="Nie znaleziono typu adresu"
                    label="Typ adresu"
                    outlined
                    dense
                    return-object
                    :error-messages="addressTypeErrors"
                    required
                />
                <v-text-field
                    v-model="customerAddress.order"
                    label="Kolejność"
                    type="number"
                    :error-messages="orderErrors"
                    :min="0"
                    outlined
                    dense
                />
            </v-card-text>

            <v-divider />

            <v-card-actions>
                <v-btn
                    color="error"
                    text
                    data-sentry-label="Anuluj"
                    @click="onClose()"
                >
                    Anuluj
                </v-btn>
                <v-spacer />
                <v-btn
                    color="success"
                    text
                    data-sentry-label="Zapisz"
                    :loading="actionLoading"
                    @click="saveCustomerAddress()"
                >
                    Zapisz
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import { required, integer, minValue } from 'vuelidate/lib/validators'
import parse from '@/mixins/parse.js'
import { isEmpty } from 'lodash'

export default {
    name: 'CustomerAddressDialog',
    mixins: [
        parse
    ],
    props: {
        isOpen: {
            type: Boolean
        },
        customerAddressData: {
            type: Object,
            default: null
        },
        customerContactData: {
            type: Object,
            default: null
        },
        customerData: {
            type: Object,
            default: null
        },
    },
    data() {
        return {
            actionLoading: false,
            customerAddress: {
                contacts: []
            },
            customerContact: {}
        }
    },
    validations() {
        return {
            customerAddress: {
                street: {
                    required
                },
                postCode: {
                    required
                },
                city: {
                    required
                },
                country: {
                    required
                },
                addressType: {
                    required
                },
                order: {
                    integer,
                    minValue: minValue(0)
                },
            }
        }
    },
    computed: {
        isNewCustomerAddress() {
            return isEmpty(this.customerAddressData)
        },
        countries() {
            return this.$store.state.customers.customers.countries
        },
        addressTypes() {
            return this.$store.state.customers.customers.addressTypes
        },
        customerContacts() {
            return this.$store.state.customers.customers.customerContacts
        },
        streetErrors() {
            const errors = []

            if (!this.$v.customerAddress.street.$dirty) return errors
            !this.$v.customerAddress.street.required && errors.push('Proszę podać ulicę.')

            return errors
        },
        postCodeErrors() {
            const errors = []

            if (!this.$v.customerAddress.postCode.$dirty) return errors
            !this.$v.customerAddress.postCode.required && errors.push('Proszę podać kod.')

            return errors
        },
        cityErrors() {
            const errors = []

            if (!this.$v.customerAddress.city.$dirty) return errors
            !this.$v.customerAddress.city.required && errors.push('Proszę podać miasto.')

            return errors
        },
        countryErrors() {
            const errors = []

            if (!this.$v.customerAddress.country.$dirty) return errors
            !this.$v.customerAddress.country.required && errors.push('Proszę wybrać kraj.')

            return errors
        },
        orderErrors() {
            const errors = []

            if (!this.$v.customerAddress.order.$dirty) return errors
            if ((!this.$v.customerAddress.order.integer || !this.$v.customerAddress.order.minValue)) {
                errors.push('Proszę podać dodatnią liczbę całkowitą.')
            }

            return errors
        },
        addressTypeErrors() {
            const errors = []

            if (!this.$v.customerAddress.addressType.$dirty) return errors
            !this.$v.customerAddress.addressType.required && errors.push('Proszę wybrać typ adresu.')

            return errors
        },
    },
    watch: {
        isOpen(boolean) {
            if (boolean) {
                this.customerAddress = JSON.parse(JSON.stringify(this.customerAddressData))
            }
        },
    },
    methods: {
        saveCustomerAddress() {
            this.$v.$touch()
            if (this.$v.$invalid) {
                this.$store.dispatch('showNotification', {
                    type: 'error',
                    message: this.$i18n.t('validations.invalidMessage')
                })

                return
            }
            this.actionLoading = true
            this.customerAddress.countryId = this.customerAddress.country.id
            this.customerAddress.addressTypeId = this.customerAddress.addressType.id
            if (this.isNewCustomerAddress) this.customerAddress.contacts = []
            this.$emit('on-confirm', this.customerAddress)
            this.onClose()
        },
        onClose() {
            this.actionLoading = false
            this.$emit('on-close')
            this.$v.$reset()
            setTimeout(() => {
                this.customerAddress = {}
                this.customerContact = {}
            }, 500)
        },
    }
}
</script>

